import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import {
    AppliedBillableConceptCreateRequest,
    AppliedBillableConceptExtraFieldsCreateRequest,
    AppliedBillableConceptExtraFieldsPatchInBatchRequest,
    AppliedBillableConceptPatchInBatchRequest,
    AppliedBillableConceptResponse,
    BillingCopaymentType
} from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { BillableConceptResponse } from '@/api/services/billing/common-billable-concept/billing-common-billable-concept.dto'
import { billingCommonBillableConceptService } from '@/api/services/billing/common-billable-concept/billing-common-billable-concept.service'
import { BillableRateWithConceptInformationResponse } from '@/api/services/billing/common-billable-rate/billing-common-billable-rate.dto'
import { billingCommonBillableRateService } from '@/api/services/billing/common-billable-rate/billing-common-billable-rate.service'
import { CustomerResponse } from '@/api/services/billing/common-customer/billing-common-customer.dto'
import { billingCommonCustomerService } from '@/api/services/billing/common-customer/billing-common-customer.service'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import { initialValuableConceptsModalGridBasicFormState, ValuableConceptsModalGridBasicFormInfo, ValuableConceptsModalGridBasicFormState } from '@/components/valuable-concepts-modal/valuable-concepts-modal-grid-basic-form/valuable-concepts-modal-grid-basic-form.model'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { GridUtils } from '@/shared/util/grid-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { BModal } from 'bootstrap-vue/src/components/modal'
import { isNil } from 'lodash'
import { Component, Emit, Prop, Ref, Vue } from 'vue-property-decorator'

@Component({
    components: {
        AlertBanner
    }
})
export default class ValuableConceptsModalGridBasicForm extends Vue {
    @Ref() readonly valuableConceptsModalGridBasicForm!: BModal
    @Prop() readonly actionTitle!: string

    @Emit()
    reloadView(): void {}

    @Emit()
    reloadGrid(): void {}

    state: ValuableConceptsModalGridBasicFormState
    constructor() {
        super()
        this.state = initialValuableConceptsModalGridBasicFormState()
    }

    get entityTypeLabel(): string {
        return `${this.state.info.nodeSelected?.fullLabel ?? '-'} ${this.state.info.nodeSelected?.entityTypeLabel ?? '-'}`
    }

    get isUpdateForm(): boolean {
        return !!this.state.info.rowSelected?.id
    }

    get canSelectCustomers(): boolean {
        return !!this.state.selectedConcept?.id
    }

    get canSelectRates(): boolean {
        return !!this.state.customers.selected?.id
    }

    get isAutomaticConcept(): boolean {
        return this.isUpdateForm && !!this.state.info.rowSelected?.automatic
    }

    get validatedModel(): boolean {
        const conceptSelected = !!this.state.selectedConcept
        const hasErrors = this.$validator.errors.any()
        const isDirty = this.$_someFieldIsDirty(this.fields)
        const reasonNotAddInvoiceExists = !!this.state.reasonIfNotToAddToInvoice?.length
        const reasonError = !!this.state.reasonError?.length
        if (this.state.notToAddToInvoice && this.state.error) {
            return !hasErrors && isDirty && conceptSelected && reasonNotAddInvoiceExists && reasonError
        }
        if (this.state.notToAddToInvoice && !this.state.error) {
            return !hasErrors && isDirty && conceptSelected && reasonNotAddInvoiceExists
        }
        if (!this.state.notToAddToInvoice && this.state.error) {
            return !hasErrors && isDirty && conceptSelected && reasonError
        }
        return !hasErrors && isDirty && conceptSelected
    }

    get aliasRate(): string {
        return this.state.rates.selected?.alias ?? ''
    }

    get referenceRate(): string {
        return this.state.rates.selected?.referenceCode ?? ''
    }

    get isRateSelected(): boolean {
        return !!this.state.rates.selected
    }

    get requiresAuthorizaton(): boolean {
        return !!this.state.rates.selected?.requiresAuthorization
    }

    get codeAuthorization(): string {
        return this.state.info.rowSelected?.authorizationCode ?? ''
    }

    get hasAuthorization(): boolean {
        return this.state.rates.selected?.requiresAuthorization ?? false
    }

    get showRequestAuthorization(): boolean {
        return this.isUpdateForm && !!this.state.rates.selected?.requiresAuthorization && !this.codeAuthorization && !this.showRequestedAuthorization
    }

    get showRequestedAuthorization(): boolean {
        return this.state.info.rowSelected?.authorizationRequested ?? false
    }

    get deliveryNote(): string {
        return this.state.info.rowSelected?.deliveryNote?.label ?? ''
    }

    get enableNoAddInvoice(): boolean {
        return !this.state.info.rowSelected?.deliveryNote
    }

    get isEditableValueRate(): boolean {
        return this.state.info.isBillingModule ? this.isRateSelected : false
    }

    get hasDeliveryNote(): boolean {
        return !!this.state.info.rowSelected?.deliveryNote
    }

    get isBillingModule(): boolean {
        return this.state.info.isBillingModule ?? false
    }

    get disableWhenIsCopaymentConcept(): boolean {
        return !this.isBillingModule && this.state.isCopayment
    }

    get disableWhenIsCopaymentConceptInBilling(): boolean {
        return this.isBillingModule && this.state.isCopayment
    }

    get disableWhenIsCopaymentConceptInBillingAndCopaymentTypeIsCustomer(): boolean {
        return this.isBillingModule && this.state.isCopayment && this.state.copaymentType?.code === BillingCopaymentType.CUSTOMER
    }

    get copaymentTypeName(): string {
        return this.state.copaymentType?.translatedName ?? ''
    }

    async show(info: ValuableConceptsModalGridBasicFormInfo): Promise<void> {
        this.state = {
            ...initialValuableConceptsModalGridBasicFormState(),
            info
        }
        if (info.rowSelected) {
            this.state = {
                ...this.state,
                selectedConcept: info.rowSelected.concept,
                customers: {
                    ...this.state.customers,
                    selected: info.rowSelected?.customer!
                },
                notToAddToInvoice: info.rowSelected.addToInvoice,
                reasonIfNotToAddToInvoice: info.rowSelected.reasonIfNotToAddToInvoice,
                billableValue: info.rowSelected.value,
                autoInvoice: info.isBillingModule ? info.rowSelected.isForAutoInvoice : false,
                error: info.rowSelected?.autoInvoiceSystemInError ?? false,
                reasonError: info.rowSelected?.autoInvoiceSystemErrorDescription,
                authorizationCode: info.rowSelected.authorizationCode,
                hasCopayments: info.rowSelected.hasCopayments,
                isCopayment: info.rowSelected.isCopayment,
                copaymentType: info.rowSelected.copaymentType
            }
            this.state.originalBillableValue = info.rowSelected.value
            await this._getRates()
            const foundRate = this.state.rates.list.find((rateItem) => rateItem.id === info.rowSelected?.rate?.id)
            this.state = {
                ...this.state,
                rates: {
                    ...this.state.rates,
                    selected: info.rowSelected.rate ? {
                        ...info.rowSelected.rate,
                        alias: info.rowSelected.alias!,
                        referenceCode: info.rowSelected.referenceCode!,
                        requiresAuthorization: info.rowSelected.requiresAuthorization,
                        billableValue: info.rowSelected.value!,
                        copaymentValue: foundRate?.copaymentValue ?? 0,
                        hasCopayments: foundRate?.hasCopayments ?? false
                    } : null
                }
            }
        }
        this.valuableConceptsModalGridBasicForm.show()
    }

    hide(): void {
        this.valuableConceptsModalGridBasicForm.hide()
    }

    async getConcepts(): Promise<BillableConceptResponse[]> {
        const response =
            await PromiseUtils.toCallSecure(
                billingCommonBillableConceptService.getActiveBillableConcepts(this.$store.getters.labId))
        return response ?? []
    }

    async getCustomers(search: Nullable<string>): Promise<CustomerResponse[]> {
        const filter: CommonFilterModel = {
            'name': GridUtils.getTextFilterModel('contains', search)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response: StrictNullable<IResponseAgGrid<CustomerResponse>> =
            await PromiseUtils.toCallSecure(
                billingCommonCustomerService.paginator(
                    search ? filter : undefined,
                    params,
                    this.$store.getters.labId
                ))
        return response?.content ?? []
    }

    async onSelectConcept(concept: BillableConceptResponse): Promise<void> {
        this.state = {
            ...this.state,
            selectedConcept: concept,
            rates: {
                ...this.state.rates,
                selected: null
            },
            customers: {
                ...this.state.customers,
                selected: null
            },
            billableValue: null
        }
        await this._getRates()
    }

    async onSelectCustomer(customer: StrictNullable<CustomerResponse>): Promise<void> {
        this.state = {
            ...this.state,
            customers: {
                ...this.state.customers,
                selected: customer ?? null
            }
        }
        if (!this.disableWhenIsCopaymentConceptInBilling) {
            this.state = {
                ...this.state,
                rates: {
                    ...this.state.rates,
                    selected: null
                },
                billableValue: null,
                hasCopayments: false
            }
            await this._getRates()
        }
    }

    async onSelectRate(rate: StrictNullable<BillableRateWithConceptInformationResponse>): Promise<void> {
        this.state.hasCopayments = rate && !isNil(rate.hasCopayments) ? rate.hasCopayments : false
        if (rate?.hasCopayments) {
            if (rate.billableValue >= 0 && rate.copaymentValue >= 0) {
                this.state.billableValue = rate.billableValue - rate.copaymentValue
                return
            }
            this.state.billableValue = null
        } else {
            this.state.billableValue = rate?.billableValue ?? null
        }
    }

    onChangeAddToInvoice(): void {
        if (!this.state.notToAddToInvoice) {
            setTimeout(() => {
                this.state.reasonIfNotToAddToInvoice = null
            }, 50)
        }
    }

    async requestAuthorization(): Promise<void> {
        const response: StrictNullable<void> = await PromiseUtils.toCallSecure(
            billingAppliedBillableConceptService.requestAuthorization(
                this.$store.getters.labId,
                this.state.info.rowSelected?.id!
            )
        )
        if (response !== null) {
            this.state.info.rowSelected!.authorizationRequested = true
            this.reloadGrid()
        }
    }

    private _calculateParentConceptValue(): number {
        const copaymentParentValue = this.state.info.rowSelected?.copaymentParent?.value ?? 0
        const originalBillableValue = this.state.originalBillableValue ?? 0
        const currentBillableValue = this.state.billableValue ?? 0
        const copaymentParentNewValue = copaymentParentValue + (originalBillableValue - currentBillableValue)
        if (copaymentParentNewValue < 0) {
            return 0
        }
        return copaymentParentNewValue
    }

    private async _recalculateParentConcept(): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const request: AppliedBillableConceptExtraFieldsPatchInBatchRequest = {
            id: this.state.info.rowSelected?.copaymentParent?.id!,
            value: this._calculateParentConceptValue()
        }
        return this._patchWithExtraFields(request)
    }

    async doAction(): Promise<void> {
        if (this.isUpdateForm) {
            await this._updateBillableConcept()
        } else {
            await this._createBillableConcept()
        }
    }

    getTranslatedRate(item: BillableRateWithConceptInformationResponse): string {
        return `${this.$tc(item.name, 2)} (${item.accountingUnit.symbol})`
    }

    private async _patchWithExtraFields(request: AppliedBillableConceptExtraFieldsPatchInBatchRequest): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        return PromiseUtils.toCallSecure(
            billingAppliedBillableConceptService.patchWithExtraFields(
                this.$store.getters.labId,
                [request]
            )
        )
    }

    private async _updateBillableConcept(): Promise<void> {
        let response!: StrictNullable<EntityIdentifierWithErrorResponse[]>
        if (this.state.info.isBillingModule) {
            const request: AppliedBillableConceptExtraFieldsPatchInBatchRequest = {
                id: this.state.info.rowSelected?.id!,
                conceptId: this.state.selectedConcept?.id!,
                addToInvoice: !this.state.notToAddToInvoice,
                customerId: this.state.customers.selected?.id ?? null,
                rateId: this.state.rates.selected?.id ?? null,
                reasonIfNotToAddToInvoice: this.state.reasonIfNotToAddToInvoice,
                isForAutoInvoice: this.state.autoInvoice,
                value: this.state.billableValue,
                unitId: this.state.info.accountingUnit?.accountingUnit.id,
                authorizationCode: this.state.authorizationCode,
                autoInvoiceSystemInError: this.state.error,
                autoInvoiceSystemErrorDescription: this.state.reasonError
            }
            response = await this._patchWithExtraFields(request)
            if (response) {
                if (this.state.info.rowSelected?.copaymentParent) {
                    if (await ModalUtils.showConfirmationModal(this.$tc('component.valuableConceptsModal.recalculate'))) {
                        response = await this._recalculateParentConcept()
                    }
                }
            }
        } else {
            const request: AppliedBillableConceptPatchInBatchRequest = {
                id: this.state.info.rowSelected?.id!,
                conceptId: this.state.selectedConcept?.id!,
                addToInvoice: !this.state.notToAddToInvoice,
                customerId: this.state.customers.selected?.id ?? null,
                rateId: this.state.rates.selected?.id ?? null,
                reasonIfNotToAddToInvoice: this.state.reasonIfNotToAddToInvoice,
                authorizationCode: this.state.authorizationCode
            }
            response = await PromiseUtils.toCallSecure(
                billingAppliedBillableConceptService.patch(
                    this.$store.getters.labId,
                    [request]
                )
            )
        }
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            this.reloadView()
            this.hide()
        }
    }

    private async _createBillableConcept(): Promise<void> {
        let response!: StrictNullable<AppliedBillableConceptResponse>
        if (this.state.info.isBillingModule) {
            const request: AppliedBillableConceptExtraFieldsCreateRequest = {
                conceptId: this.state.selectedConcept?.id!,
                entityType: this.state.info.nodeSelected?.entityType!,
                addToInvoice: !this.state.notToAddToInvoice,
                customerId: this.state.customers.selected?.id ?? null,
                rateId: this.state.rates.selected?.id ?? null,
                reasonIfNotToAddToInvoice: this.state.reasonIfNotToAddToInvoice!,
                caseId: this.state.info.nodeSelected?.caseId!,
                sampleId: this.state.info.nodeSelected?.sampleId,
                portionId: this.state.info.nodeSelected?.portionId,
                subportionId: this.state.info.nodeSelected?.subportionId,
                isForAutoInvoice: this.state.autoInvoice,
                value: this.state.billableValue!,
                unitId: this.state.info.accountingUnit?.accountingUnit.id,
                authorizationCode: this.state.authorizationCode
            }
            response = await PromiseUtils.toCallSecure(
                billingAppliedBillableConceptService.createWithExtraFields(
                    this.$store.getters.labId,
                    request
                )
            )
        } else {
            const request: AppliedBillableConceptCreateRequest = {
                conceptId: this.state.selectedConcept?.id!,
                entityType: this.state.info.nodeSelected?.entityType!,
                addToInvoice: !this.state.notToAddToInvoice,
                customerId: this.state.customers.selected?.id ?? null,
                rateId: this.state.rates.selected?.id ?? null,
                reasonIfNotToAddToInvoice: this.state.reasonIfNotToAddToInvoice!,
                caseId: this.state.info.nodeSelected?.caseId!,
                sampleId: this.state.info.nodeSelected?.sampleId,
                portionId: this.state.info.nodeSelected?.portionId,
                subportionId: this.state.info.nodeSelected?.subportionId,
                authorizationCode: this.state.authorizationCode
            }
            response = await PromiseUtils.toCallSecure(
                billingAppliedBillableConceptService.create(
                    this.$store.getters.labId,
                    request
                )
            )
        }
        if (response) {
            this.state.info.rowSelected = response
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.reloadView()
            this.hide()
        }
    }

    private async _getRates(): Promise<void> {
        const response =
            await PromiseUtils.toCallSecure(
                billingCommonBillableRateService.getActiveBillableRates(
                    this.$store.getters.labId,
                    this.state.selectedConcept?.id!,
                    this.state.customers.selected?.id))
        this.state.rates.list = response ?? []
    }
}
